import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHead from "@/components/PageHead";
import { Button } from "@/components/ui/button";
import { characters, getCharacterDisplayName, getCharacterField } from "@/lib/characters";
import AlliterativeName from "@/components/AlliterativeName";
import PathfinderQuiz from "@/components/PathfinderQuiz";
import { useLocale } from "@/hooks/use-locale";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { trackCTAClick } from "@/lib/analytics";
import JsonLd from "@/components/JsonLd";
import { itemListSchema, breadcrumbSchema } from "@/lib/seo-schemas";

const Characters = () => {
  const { t } = useTranslation();
  const { prefix, currentLang } = useLocale();
  const [quizRef, quizVisible] = useScrollAnimation<HTMLElement>();

  const charactersList = itemListSchema({
    name: "Queebi Land Characters",
    url: "https://queebi.com/queebi-land-characters",
    items: characters.map((c) => ({
      name: getCharacterField(c, "fullName", currentLang),
      url: `https://queebi.com/${c.slug}`,
      image: c.image,
      description: getCharacterField(c, "bio", currentLang),
    })),
  });
  const breadcrumb = breadcrumbSchema([
    { name: "Home", url: "https://queebi.com/" },
    { name: "Characters", url: "https://queebi.com/queebi-land-characters" },
  ]);

  return (
    <>
      <PageHead
        title={t("characters.seoTitle")}
        description={t("characters.seoDesc")}
        canonical="https://queebi.com/queebi-land-characters"
        image="/images/og/characters-1200x630.jpg"
        imageAlt="Meet the six Queebi friends — Liam, Fabio, Kaleb, Clyde, Dana, and Casper"
        keywords="Queebi characters, emotional learning, Liam Lion, Fabio Fox, Kaleb Koala, Clyde Crocodile, Dana Deer, Casper Crab, character education, feelings for kids, emotional intelligence children, preschool emotions, animal characters for kids"
      />
      <JsonLd data={[charactersList, breadcrumb]} />

      <section
        className="py-20 md:py-32"
        style={{ backgroundImage: `url(/images/brand/bg-characters.png)`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="container">
          <h1 className="text-4xl md:text-5xl font-bold font-heading text-center mb-16">{t("characters.title")}</h1>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {characters.map((c) => {
              const displayName = getCharacterDisplayName(c, currentLang);
              const localizedBio = getCharacterField(c, "bio", currentLang);
              return (
                <div key={c.slug} className="text-center flex flex-col items-center group">
                  <img src={c.overviewImage} alt={c.fullName} className="mx-auto h-80 w-auto mb-6 transition-transform duration-300 group-hover:-translate-y-2" width={320} height={320} loading="lazy" />
                  <h2 className="text-xl md:text-2xl font-bold font-heading mb-3"><AlliterativeName fullName={c.fullName} slug={c.slug} /></h2>
                  <p className="text-base text-foreground/80 leading-relaxed mb-5 max-w-sm mx-auto">{localizedBio}</p>
                  <Button asChild className="rounded-full bg-primary text-primary-foreground font-bold mt-auto transition-transform duration-200 hover:scale-105">
                    <Link to={`${prefix}/${c.slug}`} onClick={() => trackCTAClick(`meet_${c.name}`)}>
                      {t("characters.meet", { name: displayName })}
                    </Link>
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section
        id="quiz"
        ref={quizRef}
        className={`bg-card py-20 md:py-32 transition-all duration-700 ${quizVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
      >
        <div className="container">
          <div className="flex flex-col md:flex-row items-center gap-12 mb-16">
            <div className="flex-1">
              <img src="/images/pages/quiz-hero.png" alt="Queebi Pathfinder Quiz" className="max-w-sm mx-auto" width={400} height={400} loading="lazy" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <p className="text-base font-bold text-accent uppercase tracking-wide mb-3">{t("characters.quizLabel")}</p>
              <h2 className="text-2xl md:text-3xl font-bold font-heading mb-6">{t("characters.quizTitle")}</h2>
              <p className="text-muted-foreground text-lg">{t("characters.quizDesc")}</p>
            </div>
          </div>
          <PathfinderQuiz />
        </div>
      </section>
    </>
  );
};

export default Characters;
